/**
 * `useScribeSession` (Phase 2 item 2.15 of the AI Scribe rollout)
 *
 * Single hook that owns the scribe session lifecycle on the client:
 *   - `start()`   → POST `/scribe/sessions/:encounterId/start`; persists the
 *                   returned `sessionId` in `<CallSessionProvider>` state.
 *   - `pause()`   → local-only state transition ('streaming' → 'paused'). The
 *                   middleware doesn't need to know about pause — the doctor
 *                   simply stops feeding the transcript buffer; the server
 *                   session stays open and is reusable on resume.
 *   - `resume()`  → local-only state transition ('paused' → 'streaming').
 *   - `finalize()`→ POST `/scribe/sessions/:encounterId/finalize`; clears the
 *                   provider's `scribeSessionId` and returns the state to
 *                   'idle'. Triggered by the Stop button.
 *
 * State lives in `<CallSessionProvider>`
 *  - Both `<ScribeControls>` and the inline `<Indicator>` recording badge
 *    need to read `scribeState` from the same source. Local `useState` here
 *    would split that across each consumer; instead the hook reads from and
 *    dispatches into the provider's reducer (see `callSessionReducer.ts`).
 *  - The TanStack-Query mutation flags (`isPending`) stay hook-local — the
 *    consuming `<ScribeControls>` cares about loading state and nothing else
 *    in the app needs it.
 */
import { useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { useCallSession } from '@/components/session'
import { useClient } from '@/hooks/useClient'
import { clearActiveEncounterMarker } from '@/components/session/scribeActiveEncounterStorage'
import { logError } from '@/utils/errorUtils'
import type { ScribeState } from '@/components/session/callSessionReducer'

export type { ScribeState }

export type UseScribeSessionResult = {
  /** Lifecycle state. Drives `<ScribeControls>` button visibility + the inline `<Indicator>` badge. */
  scribeState: ScribeState
  /** True while the start mutation is in flight. Drives Start-button loading state. */
  isStarting: boolean
  /** True while the finalize mutation is in flight. Drives Stop-button loading state. */
  isFinalizing: boolean
  /** Start a new scribe session against the active encounter. */
  start: () => Promise<void>
  /** Local pause — keeps the server session open but signals the UI to stop accepting captions. */
  pause: () => void
  /** Local resume — reverses pause. */
  resume: () => void
  /** Finalize the server session and reset local state to 'idle'. */
  finalize: () => Promise<void>
}

export function useScribeSession(): UseScribeSessionResult {
  const client = useClient()
  const {
    currentEncounterId,
    scribeSessionId,
    scribeState,
    encounterLanguage,
    setScribeSessionId,
    setScribeState,
    setEncounterLanguage,
  } = useCallSession()

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!currentEncounterId) {
        throw new Error('Cannot start scribe session: no active encounter')
      }
      return client.scribeStartSession({
        encounterId: currentEncounterId,
        language: encounterLanguage,
      })
    },
    onSuccess: (data) => {
      setScribeSessionId(data.sessionId)
      setScribeState('streaming')
      // An adopted live session keeps the language it was started with.
      if (data.language !== encounterLanguage) setEncounterLanguage(data.language)
    },
    onError: (err) => {
      logError('useScribeSession', 'Start scribe session failed', err)
      showNotification({
        color: 'red',
        title: 'Scribe error',
        message: 'Could not start the scribe session. Please try again.',
      })
    },
  })

  const finalizeMutation = useMutation({
    mutationFn: async () => {
      if (!currentEncounterId) {
        throw new Error('Cannot finalize scribe session: no active encounter')
      }
      if (!scribeSessionId) {
        // Defensive: the Stop button is hidden when there's no session, but
        // the doctor could conceivably double-click before state propagates.
        throw new Error('Cannot finalize scribe session: no active session id')
      }
      return client.scribeFinalizeSession({
        encounterId: currentEncounterId,
        sessionId: scribeSessionId,
      })
    },
    onSuccess: () => {
      setScribeSessionId(null)
      setScribeState('idle')
      // The session is finalized — drop the interrupted-visit recovery marker
      // so a later reload doesn't offer to resume a session that's done.
      clearActiveEncounterMarker()
      showNotification({
        color: 'green',
        message: 'Scribe session finalized.',
      })
    },
    onError: (err) => {
      logError('useScribeSession', 'Finalize scribe session failed', err)
      showNotification({
        color: 'red',
        title: 'Scribe error',
        message: 'Could not finalize the scribe session. Please try again.',
      })
    },
  })

  const start = useCallback(async () => {
    await startMutation.mutateAsync()
  }, [startMutation])

  const finalize = useCallback(async () => {
    await finalizeMutation.mutateAsync()
  }, [finalizeMutation])

  const pause = useCallback(() => {
    // Pause only meaningful while we're actively streaming. Any other state
    // is a no-op rather than an error so the consumer doesn't have to guard
    // on scribeState before dispatching.
    if (scribeState === 'streaming') {
      setScribeState('paused')
    }
  }, [scribeState, setScribeState])

  const resume = useCallback(() => {
    if (scribeState === 'paused') {
      setScribeState('streaming')
    }
  }, [scribeState, setScribeState])

  return {
    scribeState,
    isStarting: startMutation.isPending,
    isFinalizing: finalizeMutation.isPending,
    start,
    pause,
    resume,
    finalize,
  }
}
